// DARK — getircall PBX raporu ile terminal sayaçlarının karşılaştırması.

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getDarkCompare, uploadDarkReport } from '../api';
import { DARK_BRANDS, tzOfBrands } from '../constants';
import { fmtDate, fmtStamp, fmtTalk, joinDot, todayInTz } from '../format';
import type { BrandId, DarkCompare, DarkRow } from '../types';
import { cardStyle, dotStyle, Icon, Skeleton } from '../ui';

/** Fark rozeti: |fark| > 20 (api'nin `over` alanı) kırmızı, diğerleri nötr. */
const diffStyle = (over: boolean): React.CSSProperties => ({
  fontSize: '11.5px',
  fontWeight: 700,
  padding: '3px 9px',
  borderRadius: '999px',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  color: over ? '#ef6d6d' : 'var(--dim)',
  background: over ? 'rgba(239,93,93,.14)' : 'var(--surface2)',
  border: '1px solid ' + (over ? 'rgba(239,93,93,.32)' : 'var(--border)'),
});

/** Fark etiketi — U+2212 eksi ve ± işaretleri (ASCII değil). */
const diffLabel = (row: DarkRow) => {
  const d = row.darkTotal - row.terminalTotal;
  return (d > 0 ? '+' : d < 0 ? '−' : '±') + Math.abs(d);
};

/** İlk/son çağrı DARK hücresi: terminalle uyuşmazsa amber + kalın. */
const darkTimeStyle = (mismatch: boolean): React.CSSProperties => ({
  fontVariantNumeric: 'tabular-nums',
  color: mismatch ? '#e0a23c' : 'var(--text)',
  fontWeight: mismatch ? 700 : 500,
});

const gridCols = '1fr 78px 78px 92px 92px 104px 104px';

/** DARK yalnız Vegas+Prime dünyası (DARK_BRANDS) → günü daima İstanbul diliminde. */
const todayDark = () => todayInTz('Europe/Istanbul');

export default function Dark({ brands, onGoSettings }: { brands: BrandId[]; onGoSettings: () => void }) {
  const [dkDate, setDkDate] = useState<string>(() => todayDark());
  const [dkData, setDkData] = useState<DarkCompare | null>(null);
  const [dkLoading, setDkLoading] = useState(true);
  const [dkDragOver, setDkDragOver] = useState(false);
  const [dkUploading, setDkUploading] = useState(false);
  const [dkUploadName, setDkUploadName] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const brandsRef = useRef(brands);
  brandsRef.current = brands;
  const brandKey = brands.join(',');
  const seq = useRef(0);

  const load = useCallback(async () => {
    const date = dkDate || todayDark();
    const my = ++seq.current;
    setDkLoading(true);
    // http() 5xx/ağ hatasında FIRLATIR; yakalanmayınca setDkLoading(false) atlanıyor ve sayfa
    // kalıcı olarak iskelet durumunda kalıyordu. Meşgul bayrağı finally'e ait.
    try {
      const data = await getDarkCompare(date, brandsRef.current);
      if (my !== seq.current) return; // eskiyen istek — son seçim kazanır
      setDkData(data);
    } catch {
      /* önceki veri ekranda kalır; sonraki tarih/marka seçimi yeniden dener */
    } finally {
      if (my === seq.current) setDkLoading(false);
    }
  }, [dkDate, brandKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleUpload = async (file: File | null | undefined) => {
    if (!file) return;
    setDkUploading(true);
    setDkUploadName(file.name || 'rapor');
    try {
      await uploadDarkReport(file, dkDate || todayDark());
      await load();
    } catch {
      // Yükleme reddedildi (bad_file / no_rows / ağ) — bırakma alanı "Yükleniyor…" da
      // sonsuza dek takılı kalmasın.
    } finally {
      setDkUploading(false);
      setDkDragOver(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDkDragOver(false);
    void handleUpload(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!dkDragOver) setDkDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDkDragOver(false);
  };
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // aynı dosya tekrar seçilebilsin
    void handleUpload(f);
  };

  const dropStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    padding: '26px 20px',
    borderRadius: '12px',
    border: '1.5px dashed ' + (dkDragOver ? '#3d8bff' : 'var(--border2)'),
    background: dkDragOver ? 'rgba(61,139,255,.08)' : 'var(--inset)',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'border-color .15s, background .15s',
  };

  if (dkLoading && !dkData) return <Skeleton heights={[120, 150]} />;
  if (!dkData) return null;

  const dkDateLabel = fmtDate(dkDate);
  const emptyReason =
    brands.length === 0
      ? 'Üstteki marka filtresinden en az bir marka seçin.'
      : 'Seçili markalarda temsilci bulunamadı.';

  return (
    <>
      <div style={{ ...cardStyle, padding: '16px 18px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <Icon name="upload_file" size={19} color="var(--accent)" />
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>getircall PBX rapor yükleme</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '16px' }}>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--dim)',
                marginBottom: '7px',
                letterSpacing: '.04em',
              }}
            >
              RAPOR GÜNÜ
            </label>
            <input
              type="date"
              value={dkDate}
              // Tarih temizlenirse bugüne dön: boş dkDate hem başlık etiketini
              // hem yükleme gününü boşaltırdı.
              onChange={(e) => setDkDate(e.target.value || todayDark())}
              style={{
                width: '100%',
                background: 'var(--inset)',
                border: '1px solid var(--border)',
                borderRadius: '9px',
                padding: '9px 11px',
                color: 'var(--text)',
                fontSize: '12.5px',
                outline: 'none',
                colorScheme: 'dark',
                marginBottom: '12px',
              }}
            />
            <div
              onClick={() => fileRef.current?.click()}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              style={dropStyle}
            >
              {dkUploading ? (
                <>
                  <span style={{ ...dotStyle('var(--accent)', 11), animation: 'caaPulse 1s ease-in-out infinite' }} />
                  <div style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 600 }}>Yükleniyor…</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--dim)' }}>{dkUploadName}</div>
                </>
              ) : (
                <>
                  <Icon name="cloud_upload" size={30} color="var(--accent)" />
                  <div style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 600 }}>Raporu sürükleyip bırakın</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--dim)' }}>veya seçmek için tıklayın · .xlsx / .csv</div>
                </>
              )}
            </div>
            <input type="file" ref={fileRef} accept=".xlsx,.csv" onChange={onPick} style={{ display: 'none' }} />
          </div>
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--dim)',
                marginBottom: '9px',
                letterSpacing: '.04em',
              }}
            >
              YÜKLEME GEÇMİŞİ
            </div>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: '10px',
                overflow: 'hidden',
                maxHeight: '206px',
                overflowY: 'auto',
              }}
            >
              {dkData.uploads.map((u) => (
                <div
                  key={u.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '11px',
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <Icon name={/\.csv$/i.test(u.fileName) ? 'description' : 'grid_on'} size={20} color="var(--faint)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '12.5px',
                        fontWeight: 600,
                        color: 'var(--text)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {u.fileName}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--dim)' }}>
                      {'Gün ' + fmtDate(u.date) + ' · ' + u.rowCount + ' satır'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flex: 'none' }}>
                    <div style={{ fontSize: '11.5px', color: 'var(--text)' }}>{u.uploadedBy}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>
                      {/* DARK yalnızca Vegas/Prime'dır (DARK_BRANDS — BetBooker hiç katılmaz),
                          yani diliminin İstanbul olduğu SABİTTİR. Yine de tarayıcının
                          dilimine bırakılmıyor: yurt dışından bakan bir süpervizör
                          damgayı kaydırırdı. DARK_BRANDS değişirse burası da izler. */}
                      {fmtStamp(u.uploadedAt, tzOfBrands(DARK_BRANDS))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, overflow: 'hidden', marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '15px 18px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Icon name="sync_alt" size={19} color="var(--accent)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Terminal ↔ DARK karşılaştırma</div>
            <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{dkDateLabel} · mutlak fark &gt;20 kırmızı</div>
          </div>
        </div>
        {dkData.rows.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--dim)', fontSize: '13px' }}>{emptyReason}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '760px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: '10px',
                  padding: '9px 18px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.04em',
                  color: 'var(--faint)',
                }}
              >
                <span>TEMSİLCİ</span>
                <span style={{ textAlign: 'right' }}>TERMİNAL</span>
                <span style={{ textAlign: 'right' }}>DARK</span>
                <span style={{ textAlign: 'right' }}>FARK</span>
                <span style={{ textAlign: 'right' }}>KONUŞMA</span>
                <span style={{ textAlign: 'right' }}>İLK ÇAĞRI</span>
                <span style={{ textAlign: 'right' }}>SON ÇAĞRI</span>
              </div>
              <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
                {dkData.rows.map((row) => (
                  <div
                    key={row.agentId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: gridCols,
                      gap: '10px',
                      padding: '11px 18px',
                      borderBottom: '1px solid var(--border)',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{row.name}</div>
                      <div style={{ fontSize: '10.5px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>
                        {joinDot(row.darkCode, row.department)}
                      </div>
                    </div>
                    <span
                      style={{
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: 'var(--text)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {row.terminalTotal}
                    </span>
                    <span
                      style={{
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: 'var(--text)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {row.darkTotal}
                    </span>
                    <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <span style={diffStyle(row.over)}>{diffLabel(row)}</span>
                    </span>
                    <span
                      style={{
                        textAlign: 'right',
                        fontSize: '13px',
                        color: 'var(--dim)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {fmtTalk(row.darkTalkSec)}
                    </span>
                    <div style={{ textAlign: 'right', fontSize: '11px', lineHeight: 1.5 }}>
                      <div style={{ color: 'var(--faint)' }}>
                        T <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{row.terminalFirst}</span>
                      </div>
                      <div style={{ color: 'var(--faint)' }}>
                        D <span style={darkTimeStyle(row.firstMismatch)}>{row.darkFirst}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '11px', lineHeight: 1.5 }}>
                      <div style={{ color: 'var(--faint)' }}>
                        T <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{row.terminalLast}</span>
                      </div>
                      <div style={{ color: 'var(--faint)' }}>
                        D <span style={darkTimeStyle(row.lastMismatch)}>{row.darkLast}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {dkData.unmatched.length > 0 && (
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid rgba(224,162,60,.28)',
            borderRadius: '14px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '14px 18px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <Icon name="link_off" size={19} color="#e0a23c" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Eşleşmeyen DARK KOD'ları</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>Sistemde temsilci kaydı bulunmayan kodlar</div>
            </div>
            <button
              onClick={onGoSettings}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: 'var(--accent-soft)',
                border: '1px solid var(--accent-border)',
                borderRadius: '9px',
                color: 'var(--accent-text2)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Icon name="link" size={16} />
              Ayarlar'da eşleştir
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))',
              gap: '12px',
              padding: '16px 18px',
            }}
          >
            {dkData.unmatched.map((u) => (
              <div
                key={u.darkCode}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '11px',
                  background: 'var(--inset)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '12px 13px',
                }}
              >
                <span
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '9px',
                    background: 'rgba(224,162,60,.14)',
                    border: '1px solid rgba(224,162,60,.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                  }}
                >
                  <Icon name="help" size={18} color="#e0a23c" />
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {u.darkCode}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--dim)' }}>
                    {u.darkTotal} çağrı · {fmtTalk(u.darkTalkSec)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
